import React, { useState } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendEmailVerification, sendPasswordResetEmail } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { X, Mail, Lock, User, Phone, Eye, EyeOff, Shield, AlertTriangle, CheckCircle, Send } from 'lucide-react';
import { useAlert } from '../hooks/UseAlert';
import AlertModal from './AlertModal';

const AuthModal = ({ onClose, onAuthSuccess }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: ''
  });
  const [error, setError] = useState(''); //Garde setError pour les erreurs du formulaire
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: '' });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  
  const { success, warning, confirm, alertConfig, closeAlert } = useAlert();

  // ==========================================
  // VALIDATION EMAIL
  // ==========================================
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // VALIDATION TÉLÉPHONE FRANÇAIS
  const validatePhone = (phone) => {
    const cleanPhone = phone.replace(/[\s.-]/g, '');
    const phoneRegex = /^(?:(?:\+|00)33|0)[1-9](?:[0-9]{8})$/;
    return phoneRegex.test(cleanPhone);
  };

  // ANALYSE FORCE DU MOT DE PASSE
  const analyzePasswordStrength = (password) => {
    let score = 0;
    let feedback = [];

    if (password.length >= 8) score += 1;
    else feedback.push("Au moins 8 caractères");

    if (password.length >= 12) score += 1;

    if (/[A-Z]/.test(password)) {
      score += 1;
    } else {
      feedback.push("Au moins 1 majuscule");
    }

    if (/[a-z]/.test(password)) {
      score += 1;
    } else {
      feedback.push("Au moins 1 minuscule");
    }

    if (/[0-9]/.test(password)) {
      score += 1;
    } else {
      feedback.push("Au moins 1 chiffre");
    }

    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      score += 1;
    } else {
      feedback.push("Au moins 1 caractère spécial (!@#$%...)");
    }

    const simplePatterns = ['123', 'abc', 'password', 'azerty', 'qwerty'];
    const hasSimplePattern = simplePatterns.some(pattern => 
      password.toLowerCase().includes(pattern)
    );
    
    if (hasSimplePattern) {
      score -= 2;
      feedback.push("Évitez les séquences simples");
    }

    return {
      score: Math.max(0, Math.min(6, score)),
      feedback: feedback.join(', ')
    };
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, password: newPassword });
    
    if (!isLogin && newPassword) {
      setPasswordStrength(analyzePasswordStrength(newPassword));
    }
  };

  // VALIDATION NOM
  const validateName = (name) => {
    const nameRegex = /^[a-zA-ZÀ-ÖØ-öø-ÿ\s'-]{2,50}$/;
    return nameRegex.test(name);
  };

  // VALIDATION COMPLÈTE
  const validateForm = () => {
    if (!validateEmail(formData.email)) {
      setError('Format d\'email invalide');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return false;
    }

    if (!isLogin) {
      if (!validateName(formData.name)) {
        setError('Le nom ne doit contenir que des lettres (2-50 caractères)');
        return false;
      }

      if (!validatePhone(formData.phone)) {
        setError('Numéro de téléphone invalide (format français attendu)');
        return false;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Les mots de passe ne correspondent pas');
        return false;
      }

      if (passwordStrength.score < 4) {
        setError('Mot de passe trop faible. ' + passwordStrength.feedback);
        return false;
      }

      if (!acceptTerms) {
        setError('Vous devez accepter les conditions générales');
        return false;
      }
    }

    return true;
  };

  // ENVOI EMAIL DE VÉRIFICATION
  const sendVerificationEmail = async (user) => {
    try {
      const actionCodeSettings = {
        url: window.location.origin + '/?emailVerified=true',
        handleCodeInApp: false,
      };

      await sendEmailVerification(user, actionCodeSettings);
      return true;
    } catch (error) {
      console.error('Erreur envoi email:', error);
      return false;
    }
  };

  // SOUMISSION DU FORMULAIRE
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        // ========== CONNEXION ==========
        const userCredential = await signInWithEmailAndPassword(
          auth, 
          formData.email.trim(), 
          formData.password
        );

        // Vérifier si l'email est vérifié
        if (!userCredential.user.emailVerified) {
          setError('Veuillez vérifier votre email avant de vous connecter. Vérifiez votre boîte de réception.');
          
          confirm(
            'Votre email n\'est pas encore vérifié.\n\nVoulez-vous recevoir un nouvel email de vérification ?',
            async () => {
              // Callback si l'utilisateur confirme
              const sent = await sendVerificationEmail(userCredential.user);
              if (sent) {
                success(
                  'Email de vérification envoyé !\n\nVérifiez votre boîte de réception ainsi que vos spams.',
                  'Email envoyé'
                );
              } else {
                warning(
                  'Erreur lors de l\'envoi. Réessayez plus tard.',
                  'Erreur'
                );
              }
            },
            'Email non vérifié'
          );
          
          await auth.signOut();
          setLoading(false);
          return;
        }

        // Email vérifié → Connexion réussie
        onAuthSuccess();
        
      } else {
        // ========== INSCRIPTION ==========
        const userCredential = await createUserWithEmailAndPassword(
          auth,
          formData.email.trim(),
          formData.password
        );

        const cleanPhone = formData.phone.replace(/[\s.-]/g, '');

        await setDoc(doc(db, 'users', userCredential.user.uid), {
          name: formData.name.trim(),
          email: formData.email.trim().toLowerCase(),
          phone: cleanPhone,
          createdAt: new Date().toISOString(),
          isAdmin: false,
          emailVerified: false,
          accountStatus: 'pending_verification',
          verificationEmailSentAt: new Date().toISOString()
        });

        const emailSent = await sendVerificationEmail(userCredential.user);

        if (emailSent) {
          setEmailSent(true);
          setRegisteredEmail(formData.email);
          await auth.signOut();
        } else {
          setError('Compte créé mais erreur lors de l\'envoi de l\'email. Contactez le support.');
        }
      }
    } catch (error) {
      console.error('Erreur auth:', error);
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError('Cet email est déjà utilisé');
          break;
        case 'auth/invalid-email':
          setError('Format d\'email invalide');
          break;
        case 'auth/wrong-password':
          setError('Email ou mot de passe incorrect');
          break;
        case 'auth/user-not-found':
          setError('Aucun compte associé à cet email');
          break;
        case 'auth/weak-password':
          setError('Mot de passe trop faible (min. 8 caractères)');
          break;
        case 'auth/too-many-requests':
          setError('Trop de tentatives. Réessayez dans quelques minutes');
          break;
        case 'auth/network-request-failed':
          setError('Erreur réseau. Vérifiez votre connexion');
          break;
        case 'auth/invalid-credential':
          setError('Identifiants invalides');
          break;
        default:
          setError('Une erreur est survenue. Réessayez');
      }
    } finally {
      setLoading(false);
    }
  };

  // RENVOYER EMAIL DE VÉRIFICATION
  const handleResendEmail = async () => {
    setLoading(true);
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        registeredEmail,
        formData.password
      );

      const sent = await sendVerificationEmail(userCredential.user);
      
      if (sent) {
        success(
          'Email de vérification renvoyé !\n\nVérifiez votre boîte de réception.',
          'Email envoyé'
        );
      } else {
        warning(
          'Erreur lors de l\'envoi. Réessayez plus tard.',
          'Erreur'
        );
      }

      await auth.signOut();
    } catch (error) {
      console.error('Erreur renvoi email:', error);
      
      warning(
        'Impossible de renvoyer l\'email. Vérifiez vos identifiants.',
        'Erreur'
      );
    } finally {
      setLoading(false);
    }
  };

  // MOT DE PASSE OUBLIÉ
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');

    if (!forgotEmail.trim()) {
      setForgotError('Veuillez saisir votre adresse email');
      return;
    }
    if (!validateEmail(forgotEmail.trim())) {
      setForgotError('Format d\'email invalide');
      return;
    }

    setForgotLoading(true);
    try {
      const actionCodeSettings = {
        url: window.location.origin + '/?passwordReset=true',
        handleCodeInApp: false,
      };

      await sendPasswordResetEmail(auth, forgotEmail.trim().toLowerCase(), actionCodeSettings);

      // On affiche toujours le succès, même si l'email n'existe pas (sécurité)
      setForgotSent(true);
    } catch (err) {
      console.error('Erreur reset mot de passe:', err);
      // On ne révèle PAS si l'email existe ou non — message générique
      if (err.code === 'auth/too-many-requests') {
        setForgotError('Trop de demandes. Veuillez patienter quelques minutes avant de réessayer.');
      } else if (err.code === 'auth/network-request-failed') {
        setForgotError('Erreur réseau. Vérifiez votre connexion internet.');
      } else {
        // Pour les autres erreurs (ex: invalid-email), on affiche quand même succès
        setForgotSent(true);
      }
    } finally {
      setForgotLoading(false);
    }
  };

  // INDICATEURS VISUELS
  const getPasswordStrengthColor = (score) => {
    if (score <= 2) return 'bg-red-500';
    if (score <= 4) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getPasswordStrengthLabel = (score) => {
    if (score <= 2) return 'Faible';
    if (score <= 4) return 'Moyen';
    return 'Fort';
  };

  // ÉCRAN DE CONFIRMATION EMAIL
  if (emailSent) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 relative">
          <div className="text-center">
            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Send className="w-10 h-10 text-purple-600" />
            </div>

            <h2 className="text-2xl font-semibold text-purple-900 mb-4">
              Vérifiez votre email !
            </h2>

            <p className="text-gray-600 mb-6 leading-relaxed">
              Un email de vérification a été envoyé à :<br />
              <strong className="text-purple-700">{registeredEmail}</strong>
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 text-left">
              <p className="text-sm text-blue-900 mb-2">
                <strong>Prochaines étapes :</strong>
              </p>
              <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
                <li>Ouvrez votre boîte email</li>
                <li>Cliquez sur le lien de vérification</li>
                <li>Revenez ici pour vous connecter</li>
              </ol>
            </div>

            <p className="text-xs text-gray-500 mb-6">
              ðŸ’¡ Pensez à  vérifier vos <strong>spams/courriers indésirables</strong> si vous ne recevez pas l'email.
            </p>

            <div className="space-y-3">
              <button
                onClick={onClose}
                className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Fermer
              </button>

              <button
                onClick={handleResendEmail}
                disabled={loading}
                className="w-full border-2 border-purple-600 text-purple-600 py-3 rounded-lg font-medium hover:bg-purple-50 transition-colors disabled:opacity-50"
              >
                {loading ? 'Envoi...' : 'Renvoyer l\'email'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ÉCRAN MOT DE PASSE OUBLIÉ — FORMULAIRE
  if (isForgotPassword && !forgotSent) {
    return (
      <>
        <AlertModal
          isOpen={alertConfig.isOpen}
          onClose={closeAlert}
          type={alertConfig.type}
          title={alertConfig.title}
          message={alertConfig.message}
          confirmText={alertConfig.confirmText}
          onConfirm={alertConfig.onConfirm}
          showCancel={alertConfig.showCancel}
          cancelText={alertConfig.cancelText}
        />

        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 relative">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-2">
                <Lock className="w-6 h-6 text-purple-600" />
                <h2 className="text-2xl font-light text-purple-900">Mot de passe oublié</h2>
              </div>
              <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Explication */}
            <div className="bg-purple-50 border-l-4 border-purple-400 rounded-lg p-4 mb-6">
              <p className="text-sm text-purple-800 leading-relaxed">
                Saisissez l'adresse email associée à votre compte. Nous vous enverrons un lien 
                sécurisé pour créer un nouveau mot de passe.
              </p>
            </div>

            {/* Erreur */}
            {forgotError && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{forgotError}</span>
              </div>
            )}

            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 inline mr-2" />
                  Adresse email *
                </label>
                <input
                  type="email"
                  required
                  value={forgotEmail}
                  onChange={(e) => { setForgotEmail(e.target.value); setForgotError(''); }}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none transition-all"
                  placeholder="marie@exemple.com"
                  autoComplete="email"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={forgotLoading}
                className={`w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 ${
                  forgotLoading ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                {forgotLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Envoi en cours…
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Envoyer le lien de réinitialisation
                  </>
                )}
              </button>
            </form>

            {/* Retour connexion */}
            <div className="mt-6 text-center">
              <button
                onClick={() => { setIsForgotPassword(false); setForgotEmail(''); setForgotError(''); }}
                className="text-purple-600 hover:text-purple-800 text-sm font-medium transition-colors flex items-center gap-1 mx-auto"
              >
                ← Retour à la connexion
              </button>
            </div>

            {/* Sécurité */}
            <div className="mt-6 pt-4 border-t border-gray-200 flex items-start gap-2 text-xs text-gray-500">
              <Shield className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <p>Le lien expirera après 1 heure. Pour des raisons de sécurité, nous ne confirmons pas si l'adresse existe dans notre base.</p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ÉCRAN MOT DE PASSE OUBLIÉ — CONFIRMATION
  if (isForgotPassword && forgotSent) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 relative">
          <div className="text-center">
            {/* Icône succès */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>

            <h2 className="text-2xl font-semibold text-purple-900 mb-3">Email envoyé !</h2>

            <p className="text-gray-600 mb-2 leading-relaxed">
              Si un compte existe pour l'adresse
            </p>
            <p className="font-semibold text-purple-700 mb-5 break-all">{forgotEmail}</p>
            <p className="text-gray-600 mb-6 leading-relaxed">
              vous recevrez un lien de réinitialisation dans quelques instants.
            </p>

            {/* Étapes */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 text-left rounded-r-lg">
              <p className="text-sm text-blue-900 font-semibold mb-2">Prochaines étapes :</p>
              <ol className="text-sm text-blue-800 space-y-1 ml-4 list-decimal">
                <li>Ouvrez votre boîte email</li>
                <li>Cliquez sur le lien de réinitialisation</li>
                <li>Choisissez un nouveau mot de passe sécurisé</li>
                <li>Revenez ici pour vous connecter</li>
              </ol>
            </div>

            <p className="text-xs text-gray-500 mb-6">
              💡 Pensez à vérifier vos <strong>spams / courriers indésirables</strong> si vous ne recevez pas l'email. Le lien est valable <strong>1 heure</strong>.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => { setIsForgotPassword(false); setForgotSent(false); setForgotEmail(''); }}
                className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
              >
                Retour à la connexion
              </button>
              <button
                onClick={onClose}
                className="w-full border-2 border-gray-200 text-gray-600 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // FORMULAIRE PRINCIPAL
  return (
    <>
      <AlertModal
        isOpen={alertConfig.isOpen}
        onClose={closeAlert}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText={alertConfig.confirmText}
        onConfirm={alertConfig.onConfirm}
        showCancel={alertConfig.showCancel}
        cancelText={alertConfig.cancelText}
      />

      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999] overflow-y-auto py-8">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 relative my-8 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-purple-600" />
              <h2 className="text-2xl font-light text-purple-900">
                {isLogin ? 'Connexion sécurisée' : 'Créer un compte'}
              </h2>
            </div>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Message d'erreur (garde le système existant pour les erreurs de formulaire) */}
          {error && (
            <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* INSCRIPTION : Champs supplémentaires */}
            {!isLogin && (
              <>
                {/* Nom complet */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User className="w-4 h-4 inline mr-2" />
                    Nom complet *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none transition-all"
                    placeholder="Marie Dupont"
                    minLength={2}
                    maxLength={50}
                  />
                </div>

                {/* TÃ©lÃ©phone */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Phone className="w-4 h-4 inline mr-2" />
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none transition-all"
                    placeholder="06 12 34 56 78"
                  />
                  <p className="text-xs text-gray-500 mt-1">Format français (ex: 06 12 34 56 78)</p>
                </div>
              </>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                Email *
              </label>
              <input
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none transition-all"
                placeholder="marie@exemple.com"
                autoComplete="email"
              />
            </div>

            {/* Mot de passe */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Lock className="w-4 h-4 inline mr-2" />
                Mot de passe *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={formData.password}
                  onChange={handlePasswordChange}
                  className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none transition-all"
                  placeholder="Entrez votre mot de passe"
                  minLength={8}
                  autoComplete={isLogin ? "current-password" : "new-password"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              {/* Indicateur de force (inscription uniquement) */}
              {!isLogin && formData.password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-gray-600">Force du mot de passe :</span>
                    <span className={`text-xs font-semibold ${
                      passwordStrength.score <= 2 ? 'text-red-600' : 
                      passwordStrength.score <= 4 ? 'text-yellow-600' : 
                      'text-green-600'
                    }`}>
                      {getPasswordStrengthLabel(passwordStrength.score)}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor(passwordStrength.score)}`}
                      style={{ width: `${(passwordStrength.score / 6) * 100}%` }}
                    ></div>
                  </div>
                  {passwordStrength.feedback && (
                    <p className="text-xs text-gray-500 mt-1">
                      ðŸ’¡ {passwordStrength.feedback}
                    </p>
                  )}
                </div>
              )}

              {!isLogin && (
                <p className="text-xs text-gray-500 mt-1">
                  Min. 8 caractères, avec majuscules, chiffres et caractères spéciaux
                </p>
              )}

              {/* Lien mot de passe oublié — connexion uniquement */}
              {isLogin && (
                <div className="text-right mt-1">
                  <button
                    type="button"
                    onClick={() => {
                      setIsForgotPassword(true);
                      setForgotEmail(formData.email);
                      setForgotError('');
                      setForgotSent(false);
                    }}
                    className="text-xs text-purple-600 hover:text-purple-800 hover:underline transition-colors"
                  >
                    Mot de passe oublié ?
                  </button>
                </div>
              )}
            </div>

            {/* Confirmation mot de passe (inscription uniquement) */}
            {!isLogin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Lock className="w-4 h-4 inline mr-2" />
                  Confirmer le mot de passe *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    required
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 focus:border-purple-500 focus:ring-2 focus:ring-purple-200 focus:outline-none transition-all"
                    placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                    minLength={8}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Les mots de passe ne correspondent pas
                  </p>
                )}
                {formData.confirmPassword && formData.password === formData.confirmPassword && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" />
                    Les mots de passe correspondent
                  </p>
                )}
              </div>
            )}

            {/* Acceptation CGU (inscription uniquement) */}
            {!isLogin && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={(e) => setAcceptTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                    required
                  />
                  <span className="text-xs text-gray-700">
                    J'accepte les <a href="/cgv" target="_blank" className="text-purple-600 hover:underline font-medium">conditions gÃ©nÃ©rales de vente</a> et 
                    la <a href="/politique-confidentialite" target="_blank" className="text-purple-600 hover:underline font-medium">politique de confidentialitÃ©</a>
                  </span>
                </label>
              </div>
            )}

            {/* Bouton de soumission */}
            <button
              type="submit"
              disabled={loading || (!isLogin && passwordStrength.score < 4)}
              className={`w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors flex items-center justify-center gap-2 ${
                loading || (!isLogin && passwordStrength.score < 4) ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Chargement...</span>
                </>
              ) : (
                <>
                  <Shield className="w-5 h-5" />
                  <span>{isLogin ? 'Se connecter' : 'Créer mon compte sécurisé'}</span>
                </>
              )}
            </button>
          </form>

          {/* Bascule connexion/inscription */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setFormData({
                  email: '',
                  password: '',
                  confirmPassword: '',
                  name: '',
                  phone: ''
                });
                setPasswordStrength({ score: 0, feedback: '' });
                setAcceptTerms(false);
              }}
              className="text-purple-600 hover:text-purple-800 text-sm font-medium transition-colors"
            >
              {isLogin ? "Pas encore de compte ? S'inscrire" : 'Déjà un compte ? Se connecter'}
            </button>
          </div>

          {/* Sécurité info */}
          <div className="mt-6 pt-4 border-t border-gray-200">
            <div className="flex items-start gap-2 text-xs text-gray-500">
              <Shield className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
              <p>
                Vos données sont protégées et chiffrées. Nous ne partageons jamais vos informations personnelles.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthModal;